import * as z from 'zod';
import type { ZodOpenApiPathsObject } from 'zod-openapi';
import { ReasonSchema } from './catalogue.js';
import { problemResponse } from './common.js';
import { PaymentMethodSchema } from './orders.js';

/** Payments (spec §3 `payment`, §7.2; plan 07): the list and the manual provider (PAY-03). */

export const PaymentDirectionSchema = z.enum(['in', 'refund']).meta({ id: 'PaymentDirection' });

export const PaymentSchema = z
  .object({
    id: z.uuid(),
    orderId: z.uuid(),
    invoiceId: z.uuid().nullable(),
    provider: z.string().meta({ description: 'Module code: manual, monobank, nova_post, …' }),
    method: PaymentMethodSchema,
    direction: PaymentDirectionSchema,
    reference: z.string().nullable(),
    amount: z.number().int(),
    reason: z.string().nullable(),
    receivedAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
  })
  .meta({ id: 'Payment' });

export const PaymentPageSchema = z
  .object({ items: z.array(PaymentSchema), total: z.number().int() })
  .meta({ id: 'PaymentPage' });

export const PaymentQuerySchema = z.object({
  orderId: z.uuid().optional(),
  direction: PaymentDirectionSchema.optional(),
  limit: z.coerce.number().int().positive().max(200).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});

export const ManualPaymentSchema = z
  .object({
    orderId: z.uuid(),
    amount: z.number().int().positive().max(10_000_000_000).meta({ description: 'Kopecks' }),
    method: PaymentMethodSchema,
    reference: z.string().trim().max(100).nullable().optional(),
    reason: ReasonSchema,
    receivedAt: z.iso.datetime().optional().meta({ description: 'Default: now' }),
  })
  .meta({ id: 'ManualPayment' });

export type PaymentDto = z.infer<typeof PaymentSchema>;
export type PaymentPageDto = z.infer<typeof PaymentPageSchema>;
export type PaymentQuery = z.infer<typeof PaymentQuerySchema>;
export type ManualPaymentRequest = z.infer<typeof ManualPaymentSchema>;

const json = <T extends z.ZodType>(schema: T) => ({ 'application/json': { schema } });
const errors = {
  '400': problemResponse('Invalid input'),
  '401': problemResponse('Not signed in'),
  '403': problemResponse('Missing permission'),
};

export const paymentPaths: ZodOpenApiPathsObject = {
  '/api/v1/payments': {
    get: {
      summary: 'Payments and refunds, newest first',
      requestParams: { query: PaymentQuerySchema },
      responses: { '200': { description: 'OK', content: json(PaymentPageSchema) }, ...errors },
    },
  },
  '/api/v1/payments/manual': {
    post: {
      summary:
        'Record money received outside any provider (payments.match); reason required, audited',
      requestBody: { required: true, content: json(ManualPaymentSchema) },
      responses: {
        '201': { description: 'Recorded', content: json(PaymentSchema) },
        ...errors,
        '404': problemResponse('No such order'),
        '422': problemResponse('The order is still a draft'),
      },
    },
  },
  '/api/v1/payments/manual-refund': {
    post: {
      summary: 'Record a manual refund (payments.refund); never more than came in; reason required',
      requestBody: { required: true, content: json(ManualPaymentSchema) },
      responses: {
        '201': { description: 'Recorded', content: json(PaymentSchema) },
        ...errors,
        '404': problemResponse('No such order'),
        '422': problemResponse('More than was paid'),
      },
    },
  },
};
